<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import NextButton from 'dashboard/components-next/button/Button.vue';
import baileysChannel from 'dashboard/api/channel/baileysChannel';
import BaileysControlPanel from './ControlPanel.vue';

const props = defineProps({
  inbox: { type: Object, required: true },
});

const { t } = useI18n();

const session = ref({});
const isLoading = ref(true);
const fetchedAt = ref(null);
const isStale = ref(false); // bridge não respondeu o último status → não mostrar dado fóssil
let pollHandle = null;

const STATE_TONE = {
  connected: {
    dot: 'bg-n-teal-9',
    badge: 'bg-n-teal-3 text-n-teal-12 border-n-teal-7',
  },
  qr: {
    dot: 'bg-n-amber-9',
    badge: 'bg-n-amber-3 text-n-amber-12 border-n-amber-7',
  },
  connecting: {
    dot: 'bg-n-amber-9',
    badge: 'bg-n-amber-3 text-n-amber-12 border-n-amber-7',
  },
  created: {
    dot: 'bg-n-slate-7',
    badge: 'bg-n-slate-3 text-n-slate-12 border-n-slate-7',
  },
  paused: {
    dot: 'bg-n-slate-9',
    badge: 'bg-n-slate-3 text-n-slate-12 border-n-slate-7',
  },
  disconnected: {
    dot: 'bg-n-ruby-9',
    badge: 'bg-n-ruby-3 text-n-ruby-12 border-n-ruby-7',
  },
  banned: {
    dot: 'bg-n-ruby-9',
    badge: 'bg-n-ruby-3 text-n-ruby-12 border-n-ruby-7',
  },
  absent: {
    dot: 'bg-n-ruby-9',
    badge: 'bg-n-ruby-3 text-n-ruby-12 border-n-ruby-7',
  },
  stale: {
    dot: 'bg-n-slate-7',
    badge: 'bg-n-slate-3 text-n-slate-12 border-n-slate-7',
  },
};

const stateLabel = computed(() => {
  if (isStale.value) return t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.STATE.STALE');
  return t(
    `INBOX_MGMT.SETTINGS_POPUP.BAILEYS.STATE.${(session.value.state || 'created').toUpperCase()}`
  );
});

const stateTone = computed(() => {
  if (isStale.value) return STATE_TONE.stale;
  return STATE_TONE[session.value.state] || STATE_TONE.created;
});

const connectionError = computed(() => session.value.connection_error || null);

const displayName = computed(
  () => session.value.push_name || props.inbox.name || '-'
);

const phoneFormatted = computed(() => {
  // Display as +DDI (DDD) NNNN-NNNN when it looks like a BR number; otherwise raw with leading +.
  const raw = session.value.phone;
  if (!raw) return null;
  const digits = String(raw).replace(/\D/g, '');
  if (digits.length === 13 && digits.startsWith('55')) {
    return `+${digits.slice(0, 2)} (${digits.slice(2, 4)}) ${digits.slice(4, 9)}-${digits.slice(9)}`;
  }
  if (digits.length === 12 && digits.startsWith('55')) {
    return `+${digits.slice(0, 2)} (${digits.slice(2, 4)}) ${digits.slice(4, 8)}-${digits.slice(8)}`;
  }
  return `+${digits}`;
});

const initials = computed(() => {
  const name = displayName.value;
  return (
    name
      .split(/\s+/)
      .slice(0, 2)
      .map(w => w[0]?.toUpperCase() || '')
      .join('') || '?'
  );
});

const connectedFor = computed(() => {
  if (!session.value.connected_at) return null;
  const diffMs = Date.now() - new Date(session.value.connected_at).getTime();
  if (diffMs < 0) return null;
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1)
    return t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS_SESSION.JUST_NOW');
  if (minutes < 60)
    return t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS_SESSION.MINUTES_AGO', {
      count: minutes,
    });
  const hours = Math.floor(minutes / 60);
  if (hours < 24)
    return t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS_SESSION.HOURS_AGO', {
      count: hours,
    });
  const days = Math.floor(hours / 24);
  return t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS_SESSION.DAYS_AGO', {
    count: days,
  });
});

const lastSeenRelative = computed(() => {
  if (!session.value.last_seen_at) return '-';
  const diff = Math.floor(
    (Date.now() - new Date(session.value.last_seen_at).getTime()) / 1000
  );
  if (diff < 60) return t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS_SESSION.JUST_NOW');
  if (diff < 3600)
    return t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS_SESSION.MINUTES_AGO', {
      count: Math.floor(diff / 60),
    });
  if (diff < 86400)
    return t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS_SESSION.HOURS_AGO', {
      count: Math.floor(diff / 3600),
    });
  return t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS_SESSION.DAYS_AGO', {
    count: Math.floor(diff / 86400),
  });
});

const refresh = async () => {
  try {
    const { data } = await baileysChannel.status(props.inbox.id);
    session.value = data;
    fetchedAt.value = new Date();
    isStale.value = false;
  } catch (err) {
    if (err.response?.status === 404) {
      // Sessão ausente no bridge - honesto, não "Conectado" fóssil.
      session.value = { ...session.value, state: 'absent' };
      isStale.value = false;
    } else {
      // Bridge não respondeu: marca desatualizado em vez de manter o card velho.
      isStale.value = true;
    }
  } finally {
    isLoading.value = false;
  }
};

const handleSessionUpdated = data => {
  if (data?.state) {
    session.value = { ...session.value, ...data };
    fetchedAt.value = new Date();
    return;
  }
  refresh();
};

onMounted(() => {
  refresh();
  pollHandle = setInterval(refresh, 8000);
});

onBeforeUnmount(() => {
  if (pollHandle) clearInterval(pollHandle);
});
</script>

<template>
  <div class="space-y-6">
    <!-- Stale banner: bridge não respondeu o último status (não mente "Conectado") -->
    <div
      v-if="isStale"
      class="flex items-center gap-2 p-3 rounded-xl border border-n-amber-7 bg-n-amber-3 text-sm text-n-amber-12"
    >
      <span class="i-lucide-wifi-off size-4 shrink-0" />
      {{ $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.STALE_WARNING') }}
    </div>

    <!-- Hero card -->
    <div class="p-6 rounded-2xl border border-n-weak bg-n-solid-1">
      <div class="flex items-start gap-5 flex-wrap">
        <!-- Avatar / profile picture -->
        <div class="relative shrink-0">
          <img
            v-if="session.profile_picture_url"
            :src="session.profile_picture_url"
            :alt="displayName"
            class="w-24 h-24 rounded-full object-cover ring-2 ring-n-weak shadow-sm"
            @error="session.profile_picture_url = null"
          />
          <div
            v-else
            class="w-24 h-24 rounded-full flex items-center justify-center bg-n-alpha-3 text-n-slate-12 text-3xl font-semibold ring-2 ring-n-weak"
          >
            {{ initials }}
          </div>
          <span
            class="absolute bottom-1 right-1 inline-block w-4 h-4 rounded-full ring-2 ring-n-background"
            :class="stateTone.dot"
            :title="stateLabel"
          />
        </div>

        <!-- Identity block -->
        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2 flex-wrap">
            <h2 class="text-xl font-semibold text-n-slate-12 truncate">
              {{ displayName }}
            </h2>
            <span
              class="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium border"
              :class="stateTone.badge"
            >
              <span
                class="inline-block w-1.5 h-1.5 rounded-full"
                :class="stateTone.dot"
              />
              {{ stateLabel }}
            </span>
          </div>

          <div
            v-if="phoneFormatted"
            class="mt-1.5 text-sm text-n-slate-11 font-mono"
          >
            {{ phoneFormatted }}
          </div>

          <div
            v-if="connectedFor"
            class="mt-3 flex items-center gap-2 text-xs text-n-slate-11"
          >
            <span class="i-lucide-clock size-3.5" />
            {{
              $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS_SESSION.CONNECTED_FOR', {
                time: connectedFor,
              })
            }}
          </div>
        </div>

        <NextButton
          ghost
          icon="i-lucide-refresh-ccw"
          sm
          :is-loading="isLoading"
          @click="refresh"
        />
      </div>
    </div>

    <!-- Duplicate-number block: same line already connected on another channel -->
    <div
      v-if="connectionError?.code === 'duplicate_number'"
      class="p-4 rounded-xl border border-n-ruby-7 bg-n-ruby-3 text-sm text-n-ruby-12"
    >
      {{
        $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS_SESSION.DUPLICATE_NUMBER', {
          accountId: connectionError.conflicting_account_id,
          inboxId: connectionError.conflicting_inbox_id,
        })
      }}
    </div>

    <!-- Detail grid -->
    <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div class="p-4 rounded-xl border border-n-weak bg-n-solid-1">
        <div class="text-xs text-n-slate-11 uppercase tracking-wider">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS_SESSION.DISPLAY_NAME') }}
        </div>
        <div class="mt-1 text-sm font-medium text-n-slate-12">
          {{ session.push_name || '-' }}
        </div>
        <div class="mt-0.5 text-xs text-n-slate-11">
          {{
            $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS_SESSION.DISPLAY_NAME_HELP')
          }}
        </div>
      </div>

      <div class="p-4 rounded-xl border border-n-weak bg-n-solid-1">
        <div class="text-xs text-n-slate-11 uppercase tracking-wider">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS_SESSION.PHONE') }}
        </div>
        <div class="mt-1 text-sm font-medium text-n-slate-12 font-mono">
          {{ phoneFormatted || '-' }}
        </div>
        <div class="mt-0.5 text-xs text-n-slate-11">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS_SESSION.PHONE_HELP') }}
        </div>
      </div>

      <div class="p-4 rounded-xl border border-n-weak bg-n-solid-1">
        <div class="text-xs text-n-slate-11 uppercase tracking-wider">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS_SESSION.STATUS') }}
        </div>
        <div class="mt-1 flex items-center gap-2">
          <span
            class="inline-block w-2 h-2 rounded-full"
            :class="stateTone.dot"
          />
          <span class="text-sm font-medium text-n-slate-12">{{
            stateLabel
          }}</span>
        </div>
        <div class="mt-0.5 text-xs text-n-slate-11">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS_SESSION.STATUS_HELP') }}
        </div>
      </div>

      <div class="p-4 rounded-xl border border-n-weak bg-n-solid-1">
        <div class="text-xs text-n-slate-11 uppercase tracking-wider">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS_SESSION.LAST_SEEN') }}
        </div>
        <div class="mt-1 text-sm font-medium text-n-slate-12">
          {{ lastSeenRelative }}
        </div>
        <div class="mt-0.5 text-xs text-n-slate-11">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS_SESSION.LAST_SEEN_HELP') }}
        </div>
      </div>
    </div>

    <!-- Footer hint -->
    <div class="text-xs text-n-slate-11 px-1">
      {{ $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS_SESSION.FOOTER_HINT') }}
    </div>

    <!-- Separator: control panel below -->
    <div class="border-t border-n-weak pt-6">
      <BaileysControlPanel
        :inbox="inbox"
        @session-updated="handleSessionUpdated"
      />
    </div>
  </div>
</template>
